import { useCallback, useLayoutEffect, useRef } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import {
  useDragSelect,
  type DragPoint,
  type DragRange,
} from '@/components/calendar/use-drag-select'

/** The scrolling grid a row's track sits in. */
export const GRID_SELECTOR = '[data-testid="portfolio-grid"]'

/** How far (px) a press may wander and still be a click on its day, not a drag. */
export const DRAG_SLOP = 4

/** How near (px) the grid's left or right edge a drag starts scrolling it. */
export const EDGE_ZONE = 40

/** The most an edge scroll moves the grid in one frame (px), at the very edge or past it. */
export const EDGE_MAX_STEP = 16

interface GridBox {
  left: number
  right: number
}

/**
 * The grid's scrolled area on screen: inside its border and left of its
 * vertical scrollbar (where scrollbars take space), so "the last day in view"
 * is never one under the scrollbar. Null before the grid has a width.
 */
function scrolledBox(grid: HTMLElement): GridBox | null {
  if (grid.clientWidth === 0) return null
  const left = grid.getBoundingClientRect().left + grid.clientLeft
  return { left, right: left + grid.clientWidth }
}

/**
 * How far to scroll the grid this frame for a drag whose pointer is at
 * `clientX`: a step that grows the deeper the pointer is into the zone at
 * either edge, and the most past it, negative to the left. The days' visible
 * left edge is right of the sticky property column, not the grid's own.
 */
export function edgeScrollStep(clientX: number, grid: GridBox, asideWidth: number): number {
  const leftZone = grid.left + asideWidth + EDGE_ZONE
  const rightZone = grid.right - EDGE_ZONE
  if (rightZone <= leftZone) return 0
  const step = (depth: number) =>
    Math.min(EDGE_MAX_STEP, Math.ceil((depth / EDGE_ZONE) * EDGE_MAX_STEP))
  if (clientX > rightZone) return step(clientX - rightZone)
  if (clientX < leftZone) return -step(leftZone - clientX)
  return 0
}

interface RowGeometry {
  /** The day number of the axis's first day. */
  axisStart: number
  dayCount: number
  dayWidth: number
  asideWidth: number
}

interface RowDragOptions extends RowGeometry {
  /** A drag released over more than one day: its first and last day numbers. */
  onSelect: (first: number, last: number, track: HTMLElement) => void
  /** The press became a drag, so the click that ends it is not a day. */
  onDragged: () => void
  /** A drag began. */
  onStart: () => void
}

export interface RowDrag {
  /** The day numbers to highlight, or null. */
  range: DragRange<number> | null
  /**
   * A press on an empty day of the row's track: arms a drag (mouse and pen)
   * and returns the day number pressed.
   */
  press: (event: ReactPointerEvent<HTMLElement>) => number | null
}

/**
 * Drag-to-block on one portfolio row (CAP-1419): the property tab's drag
 * (`useDragSelect`), with what a timeline adds.
 *
 * - The day under the pointer is read from the row's own track by its x alone,
 *   so the range stays on the row the press started on wherever the pointer
 *   goes up or down.
 * - Days are day numbers, never axis indices: the grid gains and drops days on
 *   its left as windows come and go, mid-drag too.
 * - Over the sticky property column, or past the grid's right edge, the day is
 *   the first or last one in view, never one hidden under the column.
 * - Near either edge the grid scrolls itself, one step a frame while the
 *   pointer stays there, and the infinite scroll reads days as usual.
 * - A press must move a few pixels (`DRAG_SLOP`) before it counts, so a press
 *   that wobbles across a day line is still a click on its day.
 */
export function useRowDrag({ onSelect, onDragged, onStart, ...geometry }: RowDragOptions): RowDrag {
  const geometryRef = useRef<RowGeometry>(geometry)
  useLayoutEffect(() => {
    geometryRef.current = geometry
  })
  const trackRef = useRef<HTMLElement | null>(null)
  const pointRef = useRef<DragPoint | null>(null)
  const frameRef = useRef(0)

  const dayAt = useCallback(({ clientX }: DragPoint): number | null => {
    const track = trackRef.current
    if (!track) return null
    const { axisStart, dayCount, dayWidth, asideWidth } = geometryRef.current
    let x = clientX
    // Held to the days in view, where the grid can say where they are.
    const grid = track.closest<HTMLElement>(GRID_SELECTOR)
    const box = grid ? scrolledBox(grid) : null
    if (box) x = Math.min(box.right - 1, Math.max(box.left + asideWidth, x))
    const index = Math.floor((x - track.getBoundingClientRect().left) / dayWidth)
    return axisStart + Math.min(dayCount - 1, Math.max(0, index))
  }, [])

  const stopScrolling = () => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current)
    frameRef.current = 0
  }

  // Assigned below, once the drag exists: each frame scrolls and then reads
  // the day under the still pointer again.
  const scrollFrameRef = useRef<() => void>(() => {})

  const drag = useDragSelect<number>({
    dayAt,
    onSelect: (first, last) => {
      const track = trackRef.current
      if (track) onSelect(first, last, track)
    },
    scope: 'row',
    slop: DRAG_SLOP,
    onDragged,
    onMove: point => {
      pointRef.current = point
      if (!frameRef.current) frameRef.current = requestAnimationFrame(scrollFrameRef.current)
    },
    onEnd: () => {
      stopScrolling()
      pointRef.current = null
    },
  })

  const { refresh } = drag
  useLayoutEffect(() => {
    scrollFrameRef.current = () => {
      frameRef.current = 0
      const point = pointRef.current
      const grid = trackRef.current?.closest<HTMLElement>(GRID_SELECTOR)
      const box = grid ? scrolledBox(grid) : null
      if (!point || !grid || !box) return
      const step = edgeScrollStep(point.clientX, box, geometryRef.current.asideWidth)
      if (!step) return
      // At the very start of the track this moves nothing until the grid has
      // grown its days to the left; the next frame scrolls on.
      grid.scrollLeft += step
      refresh()
      frameRef.current = requestAnimationFrame(scrollFrameRef.current)
    }
  }, [refresh])

  const press = (event: ReactPointerEvent<HTMLElement>) => {
    trackRef.current = event.currentTarget
    const day = dayAt({ clientX: event.clientX, clientY: event.clientY, target: event.target })
    if (day === null) return null
    if (event.button === 0 && event.pointerType !== 'touch') onStart()
    drag.start(event, day)
    return day
  }

  return { range: drag.range, press }
}
